<template>
  <FormKit @submit="onSubmit" type="form" :actions="false" :preserve-errors="true" data-testid="viewer-cta-button-form" v-model="model" id="ctaForm">
    <div
      class="fixed z-[99] overflow-y-auto top-5 right-5 bottom-5 p-6 pb-[6.875rem] shadow-lg bg-white dark:bg-zinc-800 w-full sm:w-80 border border-slate-50 dark:border-zinc-700 rounded-10"
    >
      <div class="flex flex-col gap-4">
        <h3 class="text-2xl font-semibold dark:text-zinc-300">Add Custom Button</h3>
        <p class="text-base text-slate-600 dark:text-zinc-500">
          Use a custom button to give your viewers an action they can take. For example a link to book a meeting, to a support article, or to a product you’re
          selling.
        </p>
        <div class="flex flex-col gap-2 bg-slate-100 dark:bg-zinc-700 p-3 rounded-md">
          <FormKit type="toggle" label="Display the button" name="displayAction" />
          <HorizontalDivider class="border-slate-200 dark:!border-zinc-600" />
          <Button
            @click.prevent="applyLastUsedSettings"
            :loading="latestSettingsLoader"
            :disabled="latestSettingsLoader"
            type="link"
            data-testid="viewer-cta-button-last-used"
            variant="gray"
            icon="general.lastUsed"
            >Use last settings</Button
          >
        </div>
        <HorizontalDivider />
        <FormKit type="text" validation="required:trim|length:0,255" label="Button label" name="text" placeholder="My Custom Button" />
        <FormKit type="url" data-testid="viewer-cta-input-url" validation="required:trim|url" label="Button URL" name="link" placeholder="example.com" />
        <FormKit type="colorpicker" label="Button color" name="buttonColor" />
        <FormKit type="colorpicker" label="Text color" name="textColor" />
        <FormKit label="Corner style" type="radio" name="cornerStyle" :options="cornerStyleOptions" />
        <FormKit label="Appearance" type="radio" name="appearance" @input="onAppearanceInput" :options="appearanceOptionsComputed" />
      </div>
    </div>
    <div
      class="fixed z-[100] bottom-5 right-5 w-full sm:w-80 p-6 flex gap-4 bg-white dark:bg-zinc-800 rounded-b-10 border border-slate-100 dark:border-zinc-700"
    >
      <Button :submit="true" :loading="isSaving" data-testid="viewer-cta-button-save" type="solid" variant="blue">Save &amp; Apply Button</Button>
      <Button @click.prevent="onCancel" data-testid="viewer-cta-button-cancel" type="link" variant="black">Cancel</Button>
    </div>
  </FormKit>
</template>
<script setup lang="ts">
import { Button, HorizontalDivider } from '@/storybook';
import { ref, computed, onMounted, watch, Ref } from 'vue';
import { useCallToActionStore, CallToActionItem, Appearance } from '../../store/useCallToActionStore';
import { storeToRefs } from 'pinia';
import { useToast } from '../../../../composables/useToast';
import { useViewerItemStore, ItemType } from '../../store/useViewerItemStore';

const ctaStore = useCallToActionStore();
const { cornerStyleOptions, appearanceOptions, appearanceOptionsVideo, cta, isSaving } = storeToRefs(ctaStore);
const itemStore = useViewerItemStore();
const { id } = storeToRefs(itemStore);
const appearanceOptionsComputed = computed(() => (itemStore.type === ItemType.VIDEO ? appearanceOptionsVideo.value : appearanceOptions.value));

const model: Ref<CallToActionItem | undefined> = ref(cta?.value);
const latestSettingsLoader = ref(false);
const emits = defineEmits(['close']);
const { error } = useToast();

const onSubmit = () => {
  ctaStore
    .save({ itemId: id.value })
    .then(() => {
      // omit dirty state before close
      ctaStore.backupCta();
      ctaStore.setHoverableButtonVisibility(false);
      emits('close');
    })
    .catch(error);
};

const applyLastUsedSettings = async () => {
  latestSettingsLoader.value = true;

  try {
    await ctaStore.applyLastSettings();
  } catch {
    error('There is no last used settings available');
  }

  latestSettingsLoader.value = false;
};

const onCancel = async () => {
  ctaStore.restoreCta();
  emits('close');
  ctaStore.setHoverableButtonVisibility(false);
};

const onAppearanceInput = (value: any) => {
  if (value === Appearance.HOVER) {
    ctaStore.setHoverableButtonVisibility(true);
  } else {
    ctaStore.setHoverableButtonVisibility(false);
  }
};

watch(model, value => {
  if (!value) {
    return;
  }

  ctaStore.setCta(value);
});

watch(
  // @ts-ignore - pinia allows watching refs
  cta,
  () => {
    model.value = cta?.value;
  },
  { deep: true }
);

onMounted(() => {
  ctaStore.backupCta();
  ctaStore.enableDisplayAction();

  if (model.value?.appearance === Appearance.HOVER) {
    ctaStore.setHoverableButtonVisibility(true);
  }
});
</script>
<style>
#ctaForm > .formkit-messages {
  display: none;
}
</style>
